import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import { Logo } from "@/Components/v2/atoms/Logo/Logo";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { TabV2 } from "../../atoms/Tabs/TabV2";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { EnvVar } from "@/env";
import { InfoResponse } from "@/@types/growthos-api/schema/openapi";
import { useAuth0 } from "@auth0/auth0-react";
import { AccountSelector } from "@/Components/v2/molecule/AccountSelector/AccountSelector";
import { toast } from "react-toastify";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useFeatureGate } from "@statsig/react-bindings";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsMenuIcon from "@/Components/v2/organism/Navigation/SettingsMenuIcon";
import { colors } from "@/Utils";

const ExternalTabLabel = ({ label }: { label: string }) => {
  return (
    <div>
      {label}
      <OpenInNewIcon sx={{ marginLeft: "8px", fontSize: "12px" }} />
    </div>
  );
};

export default function Navigation() {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const { loginWithPopup } = useAuth0();
  const { currentOrganization, organizationMemberships: orgMemberships, switchAccount } = useInfo();
  const { value: isScenarioBuilderV3Enabled } = useFeatureGate(featureFlag.scenarioBuilderV3);
  const { value: isGrowthCentreEnabled } = useFeatureGate(featureFlag.growthCentre);
  const { value: isMaiteChatHistoryEnabled } = useFeatureGate(featureFlag.maiteChatHistory);
  const { value: isMaiteV2Enabled } = useFeatureGate(featureFlag.maiteV2Enabled);

  const tabValues = {
    maite: "MAITE",
    maiteV2: "MAITE_V2",
    scenarios: "Scenario Builder",
    growthos: "GrowthOS",
    dataos: "DataOS",
    growthLabs: "GrowthLabs",
  };
  const growthOSRoute = isGrowthCentreEnabled ? "/growth-center" : "/channel-roi";
  const hendrenTier = currentOrganization?.configuration?.hendrenTier;
  const auth0OrgId = currentOrganization?.customerProviderId;
  const getDataOSUrl = (auth0OrgId: string) => {
    switch (EnvVar.envName) {
      case "dev":
        return `https://dev.dataos.mutinex.co/login/?organization=${auth0OrgId}`;
      case "stg":
        return `https://staging.dataos.mutinex.co/login/?organization=${auth0OrgId}`;
      default:
        return `https://dataos.mutinex.co/login/?organization=${auth0OrgId}`;
    }
  };
  const dataOsLoginUrl = getDataOSUrl(auth0OrgId || "");
  const growthLabsUrl = "https://help.growthos.mutinex.co/en/";

  // Determine tab value based on URL path
  const getTabValueFromPath = (pathname: string): string => {
    // Check for maite-v2 first (more specific) before checking for maite
    if (pathname.includes("maite-v2")) {
      return tabValues.maiteV2;
    }
    if (pathname.includes("maite")) {
      return tabValues.maite;
    }
    for (const [key, value] of Object.entries(tabValues)) {
      if (key !== "maite" && key !== "maiteV2" && pathname.includes(key)) {
        return value;
      }
    }
    return tabValues.growthos;
  };
  const [value, setValue] = useState(getTabValueFromPath(location.pathname));

  // Sync tab value with location changes
  useEffect(() => {
    setValue(getTabValueFromPath(location.pathname));
  }, [location.pathname]);

  const organizationMemberships = useMemo(() => {
    const isAuth0OrgIdSupplied = Boolean(EnvVar.auth0OrgId);
    if (isAuth0OrgIdSupplied) {
      return [];
    }

    return [...orgMemberships].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [orgMemberships]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    // Don't update tab state for external tabs that open in a new window
    if (newValue !== tabValues.dataos && newValue !== tabValues.growthLabs) {
      setValue(newValue);
    }
  };

  const handleAccountSelected = async (org: InfoResponse["currentOrganization"]) => {
    if (!org.customerProviderId) {
      toast.error(
        "Unable to switch accounts: authentication information is missing. Please contact support."
      );
      return;
    }

    try {
      await loginWithPopup({
        authorizationParams: {
          organization: org.customerProviderId,
        },
      });

      await switchAccount(org);
    } catch (error) {
      // Handle popup cancellation or authentication errors
      const errorMessage =
        error instanceof Error && error.message.includes("popup_closed")
          ? "Account switch cancelled. Please try again if you want to switch accounts."
          : "Failed to switch accounts. Please try again or contact support if the issue persists.";

      toast.error(errorMessage);
      // Don't call switchAccount on error to maintain consistent state
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderBottom: `1px solid ${theme.palette.navigation.border}`,
        paddingY: "4px",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor:
          theme.palette.mode === "dark" ? colors.bodyMain : theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          marginLeft: "30px",
          marginRight: "20px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Link to={growthOSRoute}>
          <Logo width="48px" height="48px" />
        </Link>
      </Box>
      <Tabs
        aria-label="navigation tabs"
        role="navigation"
        value={value}
        onChange={handleTabChange}
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor: theme.palette.mutinexMagenta.main,
            height: "3px",
            borderRadius: "2px",
            marginBottom: "8px",
          },
          "& .MuiButtonBase-root": {
            padding: "0 16px",
          },
        }}
      >
        <TabV2
          label={tabValues.growthos}
          value={tabValues.growthos}
          onClick={() => history.push(growthOSRoute)}
        />
        {hendrenTier !== "OFF" && isMaiteV2Enabled && (
          <TabV2
            label={tabValues.maite}
            value={tabValues.maiteV2}
            onClick={() => history.push("/maite-v2")}
          />
        )}
        {hendrenTier !== "OFF" && isMaiteChatHistoryEnabled && (
          <TabV2
            label={tabValues.maite}
            value={tabValues.maite}
            onClick={() => history.push("/maite")}
          />
        )}
        {isScenarioBuilderV3Enabled && (
          <TabV2
            label={tabValues.scenarios}
            value={tabValues.scenarios}
            onClick={() => history.push("/scenarios-v3")}
          />
        )}
        <TabV2
          label={<ExternalTabLabel label={tabValues.dataos} />}
          value={tabValues.dataos}
          onClick={() => window.open(dataOsLoginUrl, "_blank")}
        />
        <TabV2
          label={<ExternalTabLabel label={tabValues.growthLabs} />}
          value={tabValues.growthLabs}
          onClick={() => window.open(growthLabsUrl, "_blank")}
        />
      </Tabs>
      <Box
        sx={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {currentOrganization && (
          <AccountSelector
            selectedAccount={currentOrganization}
            accounts={organizationMemberships}
            onAccountSelected={handleAccountSelected}
          />
        )}
        <SettingsMenuIcon />
      </Box>
    </Box>
  );
}
